import { useMutation } from '@tanstack/react-query';
import { CalendarDays, Grid3X3, Home, Inbox, LogOut, Phone, PhoneCall, Rocket, Settings, Sparkles } from 'lucide-react';
import {
  Alert,
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  ThemeToggle,
  TopNavLayout,
  type SidebarNavItem,
} from '@buzzbee/ui';
import type { ReactNode } from 'react';
import { post } from '../lib/api';
import { Link, useRouter } from '../lib/router';
import { useMe, useRefreshMe, type Me, type WorkspaceSummary } from '../lib/session';
import { Logo } from './common';

const NAV = [
  { key: 'home', label: 'Home', to: '/', icon: <Home />, match: (p: string) => p === '/' },
  { key: 'calls', label: 'Calls', to: '/calls', icon: <Phone />, match: (p: string) => p.startsWith('/calls') },
  { key: 'bookings', label: 'Bookings', to: '/bookings', icon: <CalendarDays />, match: (p: string) => p.startsWith('/bookings') },
  { key: 'inbox', label: 'Inbox', to: '/inbox', icon: <Inbox />, match: (p: string) => p.startsWith('/inbox') },
  { key: 'test', label: 'Test call', to: '/test-call', icon: <PhoneCall />, match: (p: string) => p.startsWith('/test-call') },
  { key: 'grow', label: 'Front desk', to: '/front-desk', icon: <Sparkles />, match: (p: string) => p.startsWith('/front-desk') },
  { key: 'settings', label: 'Settings', to: '/settings', icon: <Settings />, match: (p: string) => p.startsWith('/settings') },
];

function Banners({ ws }: { ws: WorkspaceSummary }) {
  const e = ws.entitlements;
  if (!e.active) {
    return (
      <Alert variant="destructive" title="This workspace is read-only" className="mb-6">
        {e.state === 'suspended' ? 'It was suspended.' : 'Its trial or subscription has ended.'} Your data is kept.{' '}
        {ws.managedBy === 'hive' ? (
          <a className="font-medium underline" href={ws.hive?.billingUrl ?? '#'}>
            Manage in Hive
          </a>
        ) : (
          <Link className="font-medium underline" to="/settings/billing">
            Choose a plan
          </Link>
        )}
      </Alert>
    );
  }
  return (
    <>
      {ws.agent.paused ? (
        <Alert variant="warning" title="Your AI receptionist is paused" className="mb-6">
          This month's minutes reached your plan allowance plus your overage cap. <Link className="font-medium underline" to="/settings/billing">Raise the cap or upgrade</Link> to resume.
        </Alert>
      ) : null}
      {e.state === 'trialing' && e.trialDaysLeft !== null ? (
        <Alert variant="info" title={`${e.trialDaysLeft} day${e.trialDaysLeft === 1 ? '' : 's'} left in your ${e.planName} trial`} className="mb-6">
          {ws.managedBy === 'hive' ? 'Billing is managed in WRKRBZ Hive.' : <Link className="font-medium underline" to="/settings/billing">Add billing to keep your receptionist answering.</Link>}
        </Alert>
      ) : null}
      {!ws.onboarding.completed ? (
        <Alert variant="info" title="Finish setting up" className="mb-6">
          <Link className="font-medium underline" to="/onboarding">
            Continue the setup wizard
          </Link>{' '}
          to connect your phone and run a test call.
        </Alert>
      ) : null}
    </>
  );
}

function AccountMenu({ me, ws }: { me: Me; ws: WorkspaceSummary }) {
  const refresh = useRefreshMe();
  const { navigate } = useRouter();
  const signOut = useMutation({ mutationFn: () => post('/api/auth/signout'), onSuccess: async () => { await refresh(); navigate('/sign-in'); } });
  const switchWs = useMutation({ mutationFn: (id: string) => post('/api/me/workspace', { workspaceId: id }), onSuccess: () => { void refresh(); navigate('/'); } });
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2" aria-label="Account">
          <Avatar name={me.user.name} size="xs" />
          <span className="hidden max-w-[140px] truncate sm:inline">{ws.name}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>{me.user.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {me.workspaces.map((w) => (
          <DropdownMenuItem key={w.id} onSelect={() => switchWs.mutate(w.id)}>
            <span className="flex-1 truncate">{w.name}</span>
            <span className="text-xs text-muted-foreground">{w.product === 'receptionistbee' ? 'ReceptionistBee' : 'BuzzBee'}</span>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/settings/billing')}>
          <Rocket /> Plan and billing
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => signOut.mutate()}>
          <LogOut /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  const me = useMe();
  const { path } = useRouter();
  const ws = me.data!.current!;
  const nav: SidebarNavItem[] = NAV.map((n) => ({
    key: n.key,
    label: n.label,
    icon: n.icon,
    active: n.match(path),
    render: ({ className, children: inner, onNavigate }) => (
      <Link to={n.to} className={className} onClick={onNavigate}>
        {inner}
      </Link>
    ),
  }));
  return (
    <TopNavLayout
      brand={
        <Link to="/" aria-label="Home">
          <Logo product={ws.brand.product} />
        </Link>
      }
      nav={nav}
      actions={
        <>
          {ws.hive?.appsUrl ? (
            <Button asChild variant="ghost" size="sm">
              <a href={ws.hive.appsUrl}>
                <Grid3X3 /> <span className="hidden md:inline">All apps</span>
              </a>
            </Button>
          ) : null}
          <ThemeToggle />
          <AccountMenu me={me.data!} ws={ws} />
        </>
      }
    >
      <Banners ws={ws} />
      {children}
      {ws.brand.showPoweredBy ? <p className="mt-16 text-center text-xs text-muted-foreground">{ws.brand.name} by WRKRBZ</p> : null}
    </TopNavLayout>
  );
}
